// v3 video formats on the web side (docs/product/spec-v3-formats.md). The shapes mirror product/core's
// src/formats/types.ts (FormatInfo, BriefField, Look, Character, VideoBriefDraft, Visual) so the web builds on its
// own; keep them in step with core. Pure helpers only (no DOM): unit-tested in formats.test.ts.
import type { Aspect, Project } from "./types";

export const FORMAT_IDS = ["ad", "documentary", "cartoon", "short-film", "math-explainer", "explainer", "short", "music-video", "slideshow", "kinetic", "podcast", "demo"] as const;
export type FormatId = (typeof FORMAT_IDS)[number];
export const FORMAT_GROUPS = ["Stories", "Marketing", "Learning", "Social", "Music", "Personal"] as const;
export type FormatGroup = (typeof FORMAT_GROUPS)[number];

export type Look = { id: string; name: string; description: string; imageStyle?: string; render: string; imageModel?: string };
export type BriefFieldKey =
  | "idea"
  | "audience"
  | "tone"
  | "durationS"
  | "aspect"
  | "look"
  | "title"
  | "variant"
  | "sources"
  | "characters"
  | "photoAssetIds"
  | "songAssetId"
  | "lyrics"
  | "cta"
  | "brand"
  | "narrator";
export type BriefField = {
  key: BriefFieldKey;
  question: string;
  required: boolean;
  input: "text" | "longtext" | "choice" | "duration" | "aspect" | "look" | "characters" | "photos" | "song";
  placeholder?: string;
  choices?: string[];
};
/** previewUrl (phase 2): the sample's silent ~480p loop, lighter than videoUrl for muted covers */
export type FormatSample = { title: string; posterUrl: string; videoUrl: string; previewUrl?: string; aspect: string; durationS: number };
export type FormatInfo = {
  id: FormatId;
  name: string;
  group: FormatGroup;
  tagline: string;
  description: string;
  examples: string[];
  status: "ready" | "beta";
  durations: number[];
  aspects: Aspect[];
  looks: Look[];
  roles: string[];
  briefFields: BriefField[];
  voices: "narrator" | "cast" | "hosts" | "none";
  motion: "stills" | "clips" | "mixed";
  music?: { default: boolean; mood: string };
  captions?: "scene" | "words" | "none";
  endCard?: "cta" | "credits" | "title" | "none";
  titleCard?: boolean;
  sample?: FormatSample | null;
};

export type Character = { id: string; name: string; look: string; voice: string; voiceId?: string; refAssetId?: string; /** web/api extra: the sheet's signed url */ refUrl?: string | null; voiceName?: string | null };

/** The title's place and design (core TitleCardBriefSchema). */
export const TITLE_FONTS = ["serif", "classic", "book", "hand", "bold", "condensed", "round", "clean"] as const;
export const TITLE_BACKGROUNDS = ["canvas", "color", "gradient", "picture"] as const;
export const TITLE_MOTIONS = ["fade", "rise", "zoom", "glow", "wipe"] as const;
export type TitleCardBrief = {
  place?: "off" | "start" | "after" | "over";
  scene?: number;
  atS?: number;
  description?: string;
  font?: (typeof TITLE_FONTS)[number];
  color?: string;
  accent?: string;
  background?: (typeof TITLE_BACKGROUNDS)[number];
  bgColor?: string;
  motion?: (typeof TITLE_MOTIONS)[number];
};

/** 2026-11-03: the closing (core ClosingBriefSchema): the kind's own end card, none, or words over a scene's picture. */
export type ClosingBrief = { place?: "auto" | "off" | "over"; scene?: number; words?: string; atS?: number; text?: string; subtitle?: string; font?: (typeof TITLE_FONTS)[number]; color?: string; accent?: string; motion?: (typeof TITLE_MOTIONS)[number] };

export type VideoBriefDraft = {
  idea?: string;
  audience?: string;
  tone?: string;
  durationS?: number;
  aspect?: Aspect;
  look?: string;
  title?: string;
  variant?: string;
  sources?: string;
  characters?: Character[];
  photoAssetIds?: string[];
  songAssetId?: string;
  lyrics?: string;
  cta?: string;
  /** the title: where it goes and how it looks (absent: a plain card at the start) */
  titleCard?: TitleCardBrief | null;
  /** 2026-11-03: the closing (absent: the kind's own end card) */
  closing?: ClosingBrief | null;
  /** 2026-11-02: how scenes change (unset = the look's older rule; new videos start on "auto") */
  transition?: TransitionChoice;
  transitionSpeed?: TransitionSpeed;
  /** 2026-11-02: subtitles and captions on, off, or as the kind does them (unset = auto) */
  captions?: CaptionChoice;
  /** 2026-11-03: the cinematic look's 2.39 bars: auto (unset) = only on a widescreen video */
  letterbox?: CaptionChoice;
};
export type TransitionChoice = "auto" | "cut" | "black" | "white" | "fade" | "mixed";
export type CaptionChoice = "auto" | "on" | "off";
export type TransitionSpeed = "quick" | "normal" | "slow";

export type MathStep = { latex: string; atS: number; replace?: boolean };
export type Visual =
  | { kind: "picture" }
  | { kind: "math"; steps: MathStep[]; plot?: { fn: string; xMin: number; xMax: number; yMin: number; yMax: number; label?: string }; caption?: string }
  | { kind: "type"; emphasis: string[] }
  | { kind: "title"; text: string; subtitle?: string }
  | { kind: "quote"; text: string; by?: string }
  | { kind: "zoom"; region?: { x: number; y: number; w: number; h: number }; callouts: { x: number; y: number; w: number; h: number; label: string; atS: number }[] };

// ------------------------------------------------------------------ helpers

export const isFormatId = (v: unknown): v is FormatId => typeof v === "string" && (FORMAT_IDS as readonly string[]).includes(v);
/** A project's format (old rows and unknown values are ads). */
export const formatOfProject = (p: Pick<Project, "format">): FormatId => (isFormatId(p.format) ? p.format : "ad");
export const isAdProject = (p: Pick<Project, "format">) => formatOfProject(p) === "ad";
/** The brief of a non-ad project (stored as a VideoBriefDraft on the same column). */
export const videoBrief = (p: Pick<Project, "brief">): VideoBriefDraft => p.brief as unknown as VideoBriefDraft;

/** Short names for badges ("Documentary", not "Animated documentary"). */
export const FORMAT_SHORT: Record<FormatId, string> = {
  ad: "Ad",
  documentary: "Documentary",
  cartoon: "Cartoon",
  "short-film": "Short film",
  "math-explainer": "Math explainer",
  explainer: "Explainer",
  short: "Short",
  "music-video": "Music video",
  slideshow: "Slideshow",
  kinetic: "Kinetic type",
  podcast: "Podcast clip",
  demo: "Product demo",
};
export const formatShort = (id: string | null | undefined) => (isFormatId(id) ? FORMAT_SHORT[id] : "Video");

/** Formats grouped in picker order (groups with nothing in them are left out; order within a group is the api's). */
export function groupFormats(list: FormatInfo[]): { group: FormatGroup; formats: FormatInfo[] }[] {
  return FORMAT_GROUPS.map((group) => ({ group, formats: list.filter((f) => f.group === group) })).filter((g) => g.formats.length);
}

/** "45 s", "2 min", "1 min 30 s". */
export function fmtLen(s: number): string {
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m} min ${r} s` : `${m} min`;
}

export const ASPECT_WORD: Record<Aspect, string> = { "9:16": "Vertical", "16:9": "Widescreen", "1:1": "Square" };
export const ASPECT_FOR: Record<Aspect, string> = { "9:16": "Shorts, Reels, TikTok", "16:9": "YouTube, the web", "1:1": "Feeds" };

/** The format's defaults merged under what the user gave (like core's defaultVideoBrief). */
export function draftFor(f: FormatInfo, given: VideoBriefDraft = {}): VideoBriefDraft {
  return { durationS: f.durations[0], aspect: f.aspects[0], look: f.looks[0]?.id, ...given };
}

const isBlank = (v: unknown) => v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
/** The required brief fields still missing, in the order the Director asks them. */
export function missingFields(f: FormatInfo, b: VideoBriefDraft): BriefField[] {
  return f.briefFields.filter((q) => q.required && isBlank((b as Record<string, unknown>)[q.key]));
}

/** A title for a new project from its idea: the first clause, trimmed to a card's width. */
export function titleFromIdea(idea: string, f?: FormatInfo): string {
  const t = idea.trim().replace(/\s+/g, " ");
  if (!t) return f ? `New ${FORMAT_SHORT[f.id].toLowerCase()}` : "New video";
  const first = t.split(/(?<=[.!?])\s|[;:\n]/)[0]!;
  return first.length > 48 ? `${first.slice(0, 46).replace(/\s+\S*$/, "")}…` : first;
}

/** Which panels a format's project screen shows. */
export function panelsFor(f: Pick<FormatInfo, "id" | "voices" | "briefFields">) {
  const has = (k: BriefFieldKey) => f.briefFields.some((q) => q.key === k);
  return {
    sources: has("sources") || f.id === "documentary" || f.id === "explainer" || f.id === "podcast" || f.id === "demo",
    cast: has("characters") || f.voices === "cast" || f.voices === "hosts",
    song: has("songAssetId"),
    photos: has("photoAssetIds"),
  };
}

/** The visual a scene draws itself (no picture needed). */
export const selfDrawn = (v: Visual | undefined | null) => !!v && (v.kind === "math" || v.kind === "type");

// ------------------------------------------------------------------ a LaTeX preview without KaTeX

export const LATEX_SYMBOLS: Record<string, string> = {
  alpha: "α", beta: "β", gamma: "γ", delta: "δ", epsilon: "ε", theta: "θ", lambda: "λ", mu: "μ", pi: "π", sigma: "σ", phi: "φ", omega: "ω", tau: "τ", rho: "ρ",
  Delta: "Δ", Sigma: "Σ", Omega: "Ω", Pi: "Π", Phi: "Φ",
  cdot: "·", times: "×", div: "÷", pm: "±", le: "≤", leq: "≤", ge: "≥", geq: "≥", ne: "≠", neq: "≠", approx: "≈", equiv: "≡",
  to: "→", rightarrow: "→", Rightarrow: "⇒", leftarrow: "←", infty: "∞", partial: "∂", nabla: "∇", int: "∫", sum: "Σ", prod: "Π",
  in: "∈", forall: "∀", exists: "∃", cdots: "⋯", ldots: "…", dots: "…", circ: "∘", degree: "°",
  lim: "lim", sin: "sin", cos: "cos", tan: "tan", log: "log", ln: "ln", exp: "exp", max: "max", min: "min",
  quad: "  ", qquad: "    ", ",": " ", ";": " ", "!": "", left: "", right: "",
};
const SUP: Record<string, string> = { "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴", "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹", "+": "⁺", "-": "⁻", "=": "⁼", "(": "⁽", ")": "⁾", n: "ⁿ", i: "ⁱ", x: "ˣ", k: "ᵏ" };
const SUB: Record<string, string> = { "0": "₀", "1": "₁", "2": "₂", "3": "₃", "4": "₄", "5": "₅", "6": "₆", "7": "₇", "8": "₈", "9": "₉", "+": "₊", "-": "₋", "=": "₌", "(": "₍", ")": "₎", n: "ₙ", i: "ᵢ", x: "ₓ", k: "ₖ" };

/** One {group} or one character at `i`, and where it ends. */
function arg(s: string, i: number): [string, number] {
  while (s[i] === " ") i++;
  if (s[i] !== "{") return [s[i] ?? "", i + 1];
  let depth = 0;
  for (let j = i; j < s.length; j++) {
    if (s[j] === "{") depth++;
    else if (s[j] === "}" && --depth === 0) return [s.slice(i + 1, j), j + 1];
  }
  return [s.slice(i + 1), s.length];
}
const script = (t: string, map: Record<string, string>, mark: string) => ([...t].every((c) => map[c]) ? [...t].map((c) => map[c]).join("") : `${mark}(${t})`);

/**
 * A readable plain-text rendering of simple LaTeX (fractions, powers, roots, Greek, operators), for the scene cards.
 * The renderer draws the real thing with KaTeX; this only has to read right at a glance.
 */
export function latexPreview(src: string): string {
  const s = src.replace(/\$+/g, "").trim();
  let out = "";
  for (let i = 0; i < s.length; ) {
    const c = s[i]!;
    if (c === "\\") {
      const m = /^\\([A-Za-z]+|[,;!{}\\ ])/.exec(s.slice(i));
      if (!m) {
        i++;
        continue;
      }
      const name = m[1]!;
      i += m[0].length;
      // a space after a command word only ends the word ("\Delta A" is ΔA)
      if (/^[A-Za-z]/.test(name) && s[i] === " ") i++;
      if (name === "frac" || name === "dfrac" || name === "tfrac") {
        const [a, j] = arg(s, i);
        const [b, k] = arg(s, j);
        const wrap = (x: string) => (/^[\w.πθ]+$/.test(x) ? x : `(${x})`);
        out += `${wrap(latexPreview(a))}⁄${wrap(latexPreview(b))}`;
        i = k;
      } else if (name === "sqrt") {
        const [a, j] = arg(s, i);
        const inner = latexPreview(a);
        out += inner.length > 1 ? `√(${inner})` : `√${inner}`;
        i = j;
      } else if (name === "text" || name === "mathrm" || name === "mathbf" || name === "operatorname") {
        const [a, j] = arg(s, i);
        out += a;
        i = j;
      } else if (name === "{" || name === "}") out += name;
      else if (name === "\\") out += "  ";
      else out += LATEX_SYMBOLS[name] ?? name;
    } else if (c === "^" || c === "_") {
      const [a, j] = arg(s, i + 1);
      out += script(latexPreview(a), c === "^" ? SUP : SUB, c);
      i = j;
    } else if (c === "{" || c === "}") i++;
    else {
      out += c;
      i++;
    }
  }
  return out.replace(/\s*([=+<>≤≥≈→×·])\s*/g, " $1 ").replace(/\s+/g, " ").replace(/\( /g, "(").replace(/ \)/g, ")").trim();
}

// ------------------------------------------------------------------ a tiny, safe function parser for plot previews

type Fn = (x: number) => number;
const FUNCS: Record<string, (v: number) => number> = { sin: Math.sin, cos: Math.cos, tan: Math.tan, exp: Math.exp, log: Math.log, ln: Math.log, sqrt: Math.sqrt, abs: Math.abs };

/**
 * Compiles a plot expression in x (+ - * / ^, parentheses, sin cos tan exp log sqrt abs, pi, e, implicit
 * multiplication like "2x") to a function, without eval. Null when it doesn't parse.
 */
export function compileFn(src: string): Fn | null {
  const flat = src.replace(/\s+/g, "");
  const toks = flat.match(/\d*\.?\d+|sin|cos|tan|exp|log|ln|sqrt|abs|pi|[a-z]|[-+*/^()]/gi);
  if (!toks || toks.join("") !== flat) return null;
  let i = 0;
  const peek = () => toks[i];
  const expr = (): Fn => {
    let l = term();
    while (peek() === "+" || peek() === "-") {
      const op = toks[i++];
      const a = l;
      const r = term();
      l = op === "+" ? (x) => a(x) + r(x) : (x) => a(x) - r(x);
    }
    return l;
  };
  const term = (): Fn => {
    let l = unary();
    for (;;) {
      const t = peek();
      if (t === "*" || t === "/") {
        i++;
        const a = l;
        const r = unary();
        l = t === "*" ? (x) => a(x) * r(x) : (x) => a(x) / r(x);
      } else if (t !== undefined && (t === "(" || /^[a-z\d.]/i.test(t))) {
        // implicit multiplication: 2x, 3sin(x), (x+1)(x-1)
        const a = l;
        const r = unary();
        l = (x) => a(x) * r(x);
      } else return l;
    }
  };
  const unary = (): Fn => {
    if (peek() === "-") {
      i++;
      const a = unary();
      return (x) => -a(x);
    }
    if (peek() === "+") i++;
    return power();
  };
  const power = (): Fn => {
    const b = atom();
    if (peek() !== "^") return b;
    i++;
    const e = unary();
    return (x) => Math.pow(b(x), e(x));
  };
  const atom = (): Fn => {
    const t = toks[i++];
    if (t === undefined) throw new Error("end");
    if (t === "(") {
      const a = expr();
      if (toks[i++] !== ")") throw new Error(")");
      return a;
    }
    if (/^\d*\.?\d+$/.test(t)) {
      const n = Number(t);
      return () => n;
    }
    const w = t.toLowerCase();
    if (w === "x") return (x) => x;
    if (w === "pi") return () => Math.PI;
    if (w === "e") return () => Math.E;
    const f = FUNCS[w];
    if (f) {
      const a = atom();
      return (x) => f(a(x));
    }
    throw new Error(`name ${t}`);
  };
  try {
    const f = expr();
    return i === toks.length ? f : null;
  } catch {
    return null;
  }
}

/** An SVG path for `fn` over the plot's window, in a w×h box (breaks the line where it leaves the window). */
export function plotPath(fn: string, win: { xMin: number; xMax: number; yMin: number; yMax: number }, w: number, h: number, steps = 120): string {
  const f = compileFn(fn);
  if (!f || win.xMax <= win.xMin || win.yMax <= win.yMin) return "";
  let d = "";
  let pen = false;
  for (let k = 0; k <= steps; k++) {
    const x = win.xMin + ((win.xMax - win.xMin) * k) / steps;
    const y = f(x);
    if (!Number.isFinite(y) || y < win.yMin - (win.yMax - win.yMin) || y > win.yMax + (win.yMax - win.yMin)) {
      pen = false;
      continue;
    }
    const px = ((x - win.xMin) / (win.xMax - win.xMin)) * w;
    const py = h - ((y - win.yMin) / (win.yMax - win.yMin)) * h;
    d += `${pen ? "L" : "M"}${px.toFixed(1)} ${py.toFixed(1)}`;
    pen = true;
  }
  return d;
}
